import { useEffect, useCallback, useRef } from 'react';
import * as THREE from 'three';
import { useShallow } from 'zustand/shallow';
import useEditorStore from '../../stores/useEditorStore';
import { TOOLS_CONFIG } from '../../constants/tools';

/**
 * Measure tool: two clicks on the model (mesh raycast), draw line, distance in cm (model scale).
 * Orbit controls remain enabled (no controls.enabled = false).
 */
const MeasureHandler = () => {
  const activeTool = useEditorStore((s) => s.activeTool);
  const [sceneRefs, points, setPoints, setDistance, setPointMeshes, setMeasureLine] = useEditorStore(
    useShallow((state) => [
      state.sceneRefs,
      state.points,
      state.setPoints,
      state.setDistance,
      state.setPointMeshes,
      state.setMeasureLine,
    ])
  );

  const pointMeshesRef = useRef([null, null]);
  const lineRef = useRef(null);

  const isMeasureActive = activeTool === TOOLS_CONFIG.SCAN_PROCESS.PREPARING_SCAN.MEASURE.id;

  const handleClick = useCallback(
    (e) => {
      if (!isMeasureActive || !sceneRefs?.shadedMesh || !sceneRefs?.scene || !sceneRefs?.camera || !sceneRefs?.renderer) return;

      const { camera, renderer, shadedMesh, scene } = sceneRefs;
      const rect = renderer.domElement.getBoundingClientRect();
      const mouse = new THREE.Vector2(
        ((e.clientX - rect.left) / rect.width) * 2 - 1,
        -((e.clientY - rect.top) / rect.height) * 2 + 1
      );
      const ray = new THREE.Raycaster();
      ray.setFromCamera(mouse, camera);
      const hits = ray.intersectObject(shadedMesh, false);
      if (!hits.length) return;

      const pt = hits[0].point.clone();

      // Start of a new measurement: clear previous line and hide previous spheres
      if (points.length === 0) {
        if (lineRef.current && sceneRefs.scene) {
          sceneRefs.scene.remove(lineRef.current);
          lineRef.current.geometry?.dispose();
          lineRef.current.material?.dispose();
          lineRef.current = null;
          setMeasureLine(null);
        }
        pointMeshesRef.current.forEach((m) => m && (m.visible = false));
      }

      const newPoints = [...points, pt];
      setPoints(newPoints);

      // Sphere marker
      let mesh = pointMeshesRef.current[newPoints.length - 1];
      if (!mesh) {
        const geo = new THREE.SphereGeometry(0.3, 16, 16);
        const mat = new THREE.MeshBasicMaterial({ color: 0x583ca3, depthTest: false });
        mesh = new THREE.Mesh(geo, mat);
        mesh.renderOrder = 999;
        scene.add(mesh);
        pointMeshesRef.current[newPoints.length - 1] = mesh;
        setPointMeshes([...pointMeshesRef.current]);
      }
      mesh.position.copy(pt);
      mesh.visible = true;

      if (newPoints.length === 2) {
        // Distance in world units = cm (after scaleModelToCentimeters)
        const distanceCm = newPoints[0].distanceTo(newPoints[1]);
        setDistance(distanceCm);

        if (!lineRef.current) {
          const geom = new THREE.BufferGeometry().setFromPoints(newPoints);
          const mat = new THREE.LineBasicMaterial({
            color: 0x583ca3,
            depthTest: false,
            linewidth: 2,
          });
          const line = new THREE.Line(geom, mat);
          line.renderOrder = 999;
          lineRef.current = line;
        }
        lineRef.current.geometry.setFromPoints(newPoints);
        scene.add(lineRef.current);
        setMeasureLine(lineRef.current);

        setPoints([]);
      }
    },
    [isMeasureActive, sceneRefs, points, setPoints, setDistance, setPointMeshes, setMeasureLine]
  );

  useEffect(() => {
    if (!isMeasureActive || !sceneRefs?.renderer) return;
    const canvas = sceneRefs.renderer.domElement;
    canvas.addEventListener('pointerdown', handleClick);
    return () => canvas.removeEventListener('pointerdown', handleClick);
  }, [isMeasureActive, sceneRefs, handleClick]);

  // Remove line and spheres from the scene and clear the store.
  const cleanup = useCallback(() => {
    const { sceneRefs: refs, measureLine: line, setMeasureLine: setLine, setPoints, setDistance, setPointMeshes } = useEditorStore.getState();
    if (refs?.scene && line) {
      refs.scene.remove(line);
      line.geometry?.dispose();
      line.material?.dispose();
    }
    if (lineRef.current) {
      lineRef.current.geometry?.dispose();
      lineRef.current.material?.dispose();
      lineRef.current = null;
    }
    setLine(null);
    setPoints([]);
    setDistance(null);
    (pointMeshesRef.current || []).forEach((m) => {
      if (m && m.parent) {
        m.parent.remove(m);
        m.geometry?.dispose();
        m.material?.dispose();
      }
    });
    pointMeshesRef.current = [null, null];
    setPointMeshes([]);
  }, []);

  // Clean up when switching away from Measure (tool deactivated) and on unmount.
  useEffect(() => {
    if (isMeasureActive) return;
    cleanup();
  }, [isMeasureActive, cleanup]);

  useEffect(() => {
    return () => cleanup();
  }, [cleanup]);

  return null;
};

export default MeasureHandler;
